import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useInviteAuth } from '../pages/Invite/InviteAuthContext';
import { X, AlertCircle } from 'lucide-react';
import API_BASE_URL, { GOOGLE_CLIENT_ID } from '../config';
import './InviteAuthModal.css';

interface InviteAuthModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

export const InviteAuthModal: React.FC<InviteAuthModalProps> = ({ isOpen, onClose, onSuccess }) => {
    const { inviteLogin, inviteRegister, inviteGoogleLogin } = useInviteAuth();
    const history = useHistory();

    const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
    const [loading, setLoading] = useState(false);
    const [localError, setLocalError] = useState<string | null>(null);

    // Form fields
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');

    const handleTabChange = (tab: 'login' | 'register') => {
        setActiveTab(tab);
        setLocalError(null);
        setEmail('');
        setPassword('');
        setName('');
        setPhone('');
    };

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            setLocalError(null);
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Load Google script and render the button dynamically
    useEffect(() => {
        if (!isOpen) return;

        const sendRemoteLog = (message: string, level: string = 'info') => {
            fetch(`${API_BASE_URL}/api/invite/auth/log`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ message, level })
            }).catch(() => {});
        };

        const initializeAndRenderGoogle = () => {
            const google = (window as any).google;
            if (google) {
                console.log('[InviteAuthModal] Initializing Google with Client ID:', GOOGLE_CLIENT_ID);
                sendRemoteLog(`[InviteAuthModal] Initializing Google with Client ID: ${GOOGLE_CLIENT_ID}`);
                google.accounts.id.initialize({
                    client_id: GOOGLE_CLIENT_ID,
                    callback: handleGoogleResponse,
                });
                const btnContainer = document.getElementById('google-modal-signin-btn');
                if (btnContainer) {
                    google.accounts.id.renderButton(
                        btnContainer,
                        { 
                            theme: 'outline', 
                            size: 'large', 
                            width: '100%',
                            text: activeTab === 'login' ? 'signin_with' : 'signup_with'
                        }
                    );
                }
            } else {
                console.warn('[InviteAuthModal] Google object not found on window');
                sendRemoteLog(`[InviteAuthModal] Google object not found on window`, 'warn');
            }
        };

        sendRemoteLog(`[InviteAuthModal] Mounting auth modal. Client ID: ${GOOGLE_CLIENT_ID}`);

        let script = document.querySelector('script[src="https://accounts.google.com/gsi/client"]') as HTMLScriptElement;
        
        const handleScriptLoad = () => {
            sendRemoteLog(`[InviteAuthModal] Google library script loaded successfully`);
            initializeAndRenderGoogle();
        };

        if (script) {
            const google = (window as any).google;
            if (google) {
                initializeAndRenderGoogle();
            } else {
                script.addEventListener('load', handleScriptLoad);
            }
        } else {
            script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            document.body.appendChild(script);
            script.addEventListener('load', handleScriptLoad);
        }

        return () => {
            if (script) {
                script.removeEventListener('load', handleScriptLoad);
            }
        };
    }, [isOpen, activeTab]); // Re-render Google button when tab changes or modal opens

    const handleGoogleResponse = async (response: any) => {
        setLoading(true);
        setLocalError(null);
        try {
            await inviteGoogleLogin(response.credential);
            onClose();
            if (onSuccess) {
                onSuccess();
            } else {
                history.push('/invite/dashboard');
            }
        } catch (err: any) {
            setLocalError(err.message || 'Google authentication failed');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLocalError(null);

        if (!email || !password || (activeTab === 'register' && !name)) {
            setLocalError('Please fill in all required fields');
            return;
        }

        setLoading(true);
        try {
            if (activeTab === 'login') {
                await inviteLogin(email, password);
            } else {
                await inviteRegister(name, email, password, phone || undefined);
            }
            onClose();
            if (onSuccess) {
                onSuccess();
            } else {
                history.push('/invite/dashboard');
            }
        } catch (err: any) {
            setLocalError(err.message || 'Authentication failed');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="invite-modal-overlay" onClick={onClose}>
            <div className="invite-modal-card glass" onClick={(e) => e.stopPropagation()}>
                <button className="invite-modal-close" onClick={onClose} aria-label="Close modal">
                    <X size={18} />
                </button>

                
                <div className="invite-modal-header">
                    <span className="invite-modal-badge gold-badge">E-INVITATION</span>
                    <h2>Plan &amp; Dispatch Invites</h2>
                    <p>Access your dashboard or create a new campaign</p>
                </div>

                <div className="invite-modal-tabs">
                    <button 
                        type="button"
                        className={`invite-modal-tab-btn ${activeTab === 'login' ? 'active' : ''}`}
                        onClick={() => handleTabChange('login')}
                    >
                        Log In
                    </button>
                    <button 
                        type="button"
                        className={`invite-modal-tab-btn ${activeTab === 'register' ? 'active' : ''}`}
                        onClick={() => handleTabChange('register')}
                    >
                        Sign Up
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="invite-modal-form">
                    {localError && (
                        <div className="invite-modal-error">
                            <AlertCircle size={16} style={{ flexShrink: 0 }} />
                            <span>{localError}</span>
                        </div>

                    )}

                    {activeTab === 'register' && (
                        <div className="invite-modal-field">
                            <label htmlFor="modal-name">Full Name *</label>
                            <input
                                type="text"
                                id="modal-name"
                                placeholder="e.g. Rahul Sharma"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                            />
                        </div>
                    )}

                    <div className="invite-modal-field">
                        <label htmlFor="modal-email">Email Address *</label>
                        <input
                            type="email"
                            id="modal-email"
                            placeholder="e.g. rahul@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </div>

                    {activeTab === 'register' && (
                        <div className="invite-modal-field">
                            <label htmlFor="modal-phone">Phone Number (Optional)</label>
                            <input
                                type="tel"
                                id="modal-phone"
                                placeholder="e.g. +91XXXXXXXXXX"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                            />
                        </div>
                    )}

                    <div className="invite-modal-field">
                        <label htmlFor="modal-password">Password *</label>
                        <input
                            type="password"
                            id="modal-password"
                            placeholder="Enter your password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                    </div>

                    <button type="submit" className="invite-modal-submit-btn" disabled={loading}>
                        {loading ? 'Processing...' : activeTab === 'login' ? 'Log In & Enter Studio' : 'Create Account & Enter'}
                    </button>

                    <div className="invite-modal-divider">
                        <span>or</span>
                    </div>

                    <div id="google-modal-signin-btn" style={{ width: '100%', display: 'flex', justifyContent: 'center' }}></div>
                </form>

                <div className="invite-modal-footer">
                    {activeTab === 'login' ? (
                        <p>Don't have an account? <span onClick={() => handleTabChange('register')} className="invite-modal-link">Sign Up</span></p>
                    ) : (
                        <p>Already have an account? <span onClick={() => handleTabChange('login')} className="invite-modal-link">Log In</span></p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InviteAuthModal;
